import React, { useState, useCallback, useMemo } from 'react';
import { DateTime } from 'luxon';
import {
  Button,
  CalendarDate,
  Card,
  Clickable,
  FormInputNumber,
  FormInputText,
  FormSelect,
  Icon,
  P,
  Popover,
  SearchInput,
  Stack,
  Tabs,
} from '@fashionnovateam/nova-ui-toolkit';
import { ItemLeadTime } from './types';
import { FieldsValidationSchema } from './validation';
import { BULK_FIELD_OPTIONS, BulkFieldConfig } from './bulk-field-config';
import { shipmentModeSelectOptions } from './constants';

type BulkFieldInputProps = {
  config: BulkFieldConfig;
  countrySelectItems: { value: string; label: string; disabled?: boolean }[];
  onApply: (value: ItemLeadTime[keyof ItemLeadTime]) => void;
};

function BulkFieldInput({
  config,
  countrySelectItems,
  onApply,
}: BulkFieldInputProps) {
  const [value, setValue] = useState<string | number>('');
  const [error, setError] = useState<string | null>(null);
  const [showCalendar, setShowCalendar] = useState(false);

  const validate = useCallback(
    (val: string | number) => {
      const normalizedVal = val === '' ? null : val;
      const schema = FieldsValidationSchema[config.field];
      if (!schema) return true;
      const result = schema.validate(normalizedVal);
      if (result.error) {
        setError(result.error.details[0].message);
        return false;
      }
      setError(null);
      return true;
    },
    [config.field],
  );

  const handleApply = useCallback(() => {
    const normalizedVal = value === '' ? null : value;
    if (validate(value)) {
      onApply(normalizedVal as ItemLeadTime[keyof ItemLeadTime]);
    }
  }, [value, validate, onApply]);

  const isApplyDisabled = useMemo(() => {
    if (value === '' || value === null || value === undefined) return true;
    const normalizedVal = value === '' ? null : value;
    const schema = FieldsValidationSchema[config.field];
    if (!schema) return false;
    const result = schema.validate(normalizedVal);
    return !!result.error;
  }, [value, config.field]);

  const selectOptions = useMemo(() => {
    if (config.optionsKey === 'country') return countrySelectItems;
    if (config.optionsKey === 'shipmentMode') return shipmentModeSelectOptions;
    return [];
  }, [config.optionsKey, countrySelectItems]);

  if (config.inputType === 'select') {
    return (
      <div className="flex flex-col gap-3 w-full">
        <FormSelect
          label=" "
          inputId={`bulk-${config.field}`}
          options={selectOptions}
          value={String(value)}
          onChangeValue={(val) => {
            setValue(val);
            const normalizedVal = val === '' ? null : val;
            const schema = FieldsValidationSchema[config.field];
            if (schema) {
              const result = schema.validate(normalizedVal);
              setError(result.error ? result.error.details[0].message : null);
            }
          }}
          error={!!error}
        />
        {error && (
          <P
            size="xs"
            color="error"
            className="break-words whitespace-normal min-w-0"
          >
            {error}
          </P>
        )}
        <Button size="sm" onClick={handleApply} disabled={isApplyDisabled}>
          Apply
        </Button>
      </div>
    );
  }

  if (config.inputType === 'date') {
    const displayDate = value
      ? new Date(String(value)).toLocaleDateString('en-US', {
          year: 'numeric',
          month: 'long',
          day: 'numeric',
          timeZone: 'UTC',
        })
      : '';

    return (
      <div className="flex flex-col gap-3 w-full">
        <Clickable onClick={() => setShowCalendar((prev) => !prev)}>
          <div className="twp nu-flex nu-items-center nu-gap-2 nu-h-9 nu-w-full nu-rounded-lg nu-border nu-border-neutral-300 nu-px-3 nu-text-sm nu-bg-neutral-0">
            <P
              size="sm"
              color={value ? 'primary' : 'disabled'}
              className="flex-1 truncate"
            >
              {displayDate || 'Select date'}
            </P>
            <Icon
              name="calendar/calendar-timer"
              size="16"
              className="text-neutral-400"
            />
          </div>
        </Clickable>
        {showCalendar && (
          <CalendarDate
            date={value ? String(value) : undefined}
            onChange={(newDate) => {
              setValue(newDate);
              setShowCalendar(false);
              const schema = FieldsValidationSchema[config.field];
              if (schema) {
                const result = schema.validate(newDate);
                setError(result.error ? result.error.details[0].message : null);
              }
            }}
            notBefore={DateTime.now().toFormat('yyyy-MM-dd')}
          />
        )}
        {error && (
          <P
            size="xs"
            color="error"
            className="break-words whitespace-normal min-w-0"
          >
            {error}
          </P>
        )}
        <Button size="sm" onClick={handleApply} disabled={isApplyDisabled}>
          Apply
        </Button>
      </div>
    );
  }

  if (config.inputType === 'number') {
    return (
      <div className="flex flex-col gap-3 w-full">
        <FormInputNumber
          label=" "
          inputId={`bulk-${config.field}`}
          value={value === '' ? undefined : Number(value)}
          onChangeValue={(val) => setValue(val ?? '')}
          onBlur={() => validate(value)}
          placeholder={config.placeholder}
          endAdornment={
            config.endAdornmentLabel ? (
              <P size="xs" color="disabled">
                {config.endAdornmentLabel}
              </P>
            ) : undefined
          }
          error={!!error}
          min={0}
        />
        {error && (
          <P
            size="xs"
            color="error"
            className="break-words whitespace-normal min-w-0"
          >
            {error}
          </P>
        )}
        <Button size="sm" onClick={handleApply} disabled={isApplyDisabled}>
          Apply
        </Button>
      </div>
    );
  }

  if (config.inputType === 'exclusivity') {
    const isExclusive = value === 'FN Exclusive';
    return (
      <div className="flex flex-col gap-3 w-full">
        <Tabs.Group type="segment">
          <Clickable
            onClick={() => {
              setValue('FN Exclusive');
              setError(null);
            }}
          >
            <Tabs.Item type="segment" isActive={isExclusive}>
              Yes
            </Tabs.Item>
          </Clickable>
          <Clickable
            onClick={() => {
              setValue('Not Exclusive');
              setError(null);
            }}
          >
            <Tabs.Item type="segment" isActive={value === 'Not Exclusive'}>
              No
            </Tabs.Item>
          </Clickable>
        </Tabs.Group>
        <Button
          size="sm"
          onClick={handleApply}
          disabled={value === '' || value === null || value === undefined}
        >
          Apply
        </Button>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-3 w-full">
      <FormInputText
        label=" "
        inputId={`bulk-${config.field}`}
        value={String(value)}
        onChangeValue={(val) => setValue(val)}
        onBlur={() => validate(value)}
        placeholder={config.placeholder}
        error={!!error}
      />
      {error && (
        <P
          size="xs"
          color="error"
          className="break-words whitespace-normal min-w-0"
        >
          {error}
        </P>
      )}
      <Button size="sm" onClick={handleApply} disabled={isApplyDisabled}>
        Apply
      </Button>
    </div>
  );
}

type BulkApplyContentProps = {
  countrySelectItems: { value: string; label: string; disabled?: boolean }[];
  onApply: (
    field: keyof ItemLeadTime,
    value: ItemLeadTime[keyof ItemLeadTime],
  ) => void;
};

function BulkApplyContent({
  countrySelectItems,
  onApply,
}: BulkApplyContentProps) {
  const [selectedField, setSelectedField] = useState<BulkFieldConfig | null>(
    null,
  );
  const [filter, setFilter] = useState('');

  const filteredOptions = useMemo(
    () =>
      BULK_FIELD_OPTIONS.filter((option) =>
        option.label.toLowerCase().includes(filter.toLowerCase()),
      ),
    [filter],
  );

  if (selectedField) {
    return (
      <Card className="w-[280px] p-3 overflow-hidden">
        <div className="flex items-center gap-2 mb-3">
          <Clickable onClick={() => setSelectedField(null)}>
            <Icon name="arrow/arrow-left" size="16" />
          </Clickable>
          <P size="sm" weight="semibold">
            {selectedField.label}
          </P>
        </div>
        <BulkFieldInput
          config={selectedField}
          countrySelectItems={countrySelectItems}
          onApply={(value) => onApply(selectedField.field, value)}
        />
      </Card>
    );
  }

  return (
    <Card className="w-[236px] overflow-hidden py-1.5">
      <div className="w-full px-1.5 pb-1.5">
        <SearchInput
          value={filter}
          onChangeValue={setFilter}
          className="w-full"
        />
      </div>
      <Stack direction="col" className="max-h-64 overflow-auto">
        {filteredOptions.map((option) => (
          <Clickable
            key={option.field}
            onClick={() => setSelectedField(option)}
            className="hover:bg-neutral-100 focus:bg-neutral-200 px-2 py-1.5 text-start"
          >
            <Stack direction="row" gap="sm" align="center">
              <Icon
                name={option.icon}
                size="14"
                className="min-w-[14px] text-neutral-400"
              />
              <P size="xsm">{option.label}</P>
            </Stack>
          </Clickable>
        ))}
      </Stack>
    </Card>
  );
}

type BulkApplyPopoverProps = {
  countrySelectItems: { value: string; label: string; disabled?: boolean }[];
  onApply: (
    field: keyof ItemLeadTime,
    value: ItemLeadTime[keyof ItemLeadTime],
  ) => void;
};

export function BulkApplyPopover({
  countrySelectItems,
  onApply,
}: BulkApplyPopoverProps) {
  return (
    <Popover position="bottom" offset={8}>
      {({ close }) => (
        <>
          <Popover.Button size="sm" color="secondary">
            <Icon name="pencil-edit/pencil-edit" />
            Bulk Apply
          </Popover.Button>
          <Popover.Content>
            <BulkApplyContent
              countrySelectItems={countrySelectItems}
              onApply={(field, value) => {
                onApply(field, value);
                close();
              }}
            />
          </Popover.Content>
        </>
      )}
    </Popover>
  );
}
